import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { Box, Grid } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { RenderInputByTypeMobile } from "../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { CustomButton } from "../../../../../components/customButton/CustomButton";
import DataGridGenerico from "../../../../../components/dataGrid/DataGridGenerico";
import { CustomDialog } from "../../../../../components/customDialog/CustomDialog";
import { generateColumnsDefaultConfiguration } from "../../../../../components/dataGrid/columnHelpers";
import { PopoverModal } from "../../../../../components/popoverModal/PopoverModal";
import moment from "moment";
import { obtenerDataImagen } from "../../../../../actions/ImagenAction";
import { DecodeBase64ToBytesArray } from "../../../../../helpers/tools";
import { toast } from "react-hot-toast";
import { ADD_SEGURO_MALA_PRAXIS, DELETE_SEGURO_MALA_PRAXIS } from "../../../../../data/graphQL/mutations/Profesionales/documentosProfesional";

export const SeguroMalaPraxis = ({
    allowedWrite,
    isNew,
    datosSeleccionFormData,
    handleChange,
    setOpenPreview,
    setViewDocumentData,
    handleClosePreview,
    idProfesional,
    refetchArchivos
}) => {
    const [openModal, setOpenModal] = useState(false);
    const [nuevoSeguroData, setNuevoSeguroData] = useState({});

    // Mutations
    const [addSeguroMalaPraxis, { loading: addingSeguro }] = useMutation(ADD_SEGURO_MALA_PRAXIS);
    const [deleteSeguroMalaPraxis, { loading: deletingSeguro }] = useMutation(DELETE_SEGURO_MALA_PRAXIS);

    const isProcessing = addingSeguro || deletingSeguro;

    const handleChangeSeguro = (name, value) => {
        setNuevoSeguroData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleChangeSeguroArchivo = async (name, value) => {
        const image = await obtenerDataImagen(value);
        handleChangeSeguro(name, {
            contentType: image?.tipo,
            archivo: image?.data,
            extension: image?.extension,
            titulo: image?.nombre
        });
    };

    const resetSeguro = () => setNuevoSeguroData({});

    const handleOpenModal = () => {
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        resetSeguro();
    };

    const handleSaveSeguro = async () => {
        const newSeguroFechaDesde = moment(nuevoSeguroData.fechaDesde, 'YYYY-MM-DD');
        const newSeguroFechaHasta = moment(nuevoSeguroData.fechaHasta, 'YYYY-MM-DD');

        if (newSeguroFechaHasta.isBefore(newSeguroFechaDesde) || newSeguroFechaHasta.isBefore(moment(), 'day')) {
            toast.error('La fecha hasta no puede ser anterior a la fecha desde o a hoy.');
            return;
        }

        try {
            const { data } = await addSeguroMalaPraxis({
                variables: {
                    input: {
                        idArchivo: nuevoSeguroData.idArchivo || 0,
                        contentType: nuevoSeguroData.seguroAdjunto?.contentType,
                        titulo: nuevoSeguroData.seguroAdjunto?.titulo,
                        archivo: nuevoSeguroData.seguroAdjunto?.archivo,
                        extension: nuevoSeguroData.seguroAdjunto?.extension,
                        fechaDesde: nuevoSeguroData.fechaDesde,
                        fechaHasta: nuevoSeguroData.fechaHasta
                    },
                    idProfesional
                }
            });

            if (data?.addSeguroMalaPraxis?.success) {
                toast.success(nuevoSeguroData.idArchivo ? 'Seguro actualizado correctamente' : 'Seguro agregado correctamente');
                refetchArchivos();
                handleCloseModal();
            } else {
                toast.error(data?.addSeguroMalaPraxis?.message || 'Error al guardar seguro');
            }
        } catch (error) {
            console.error('Error al guardar seguro:', error);
            toast.error('Error al guardar seguro');
        }
    };

    const handleDeleteArchivo = async (name) => {
        // Si tiene idArchivo, eliminar del servidor
        if (nuevoSeguroData.idArchivo) {
            try {
                const { data } = await deleteSeguroMalaPraxis({
                    variables: {
                        idArchivo: nuevoSeguroData.idArchivo
                    }
                });

                if (data?.deleteSeguroMalaPraxis?.success) {
                    toast.success('Archivo eliminado correctamente');
                    handleChangeSeguro(name, null);
                } else {
                    toast.error(data?.deleteSeguroMalaPraxis?.message || 'Error al eliminar archivo');
                }
            } catch (error) {
                console.error('Error al eliminar archivo:', error);
                toast.error('Error al eliminar archivo');
            }
        } else {
            // Si no tiene idArchivo, solo limpiar el estado local
            handleChangeSeguro(name, null);
        }
    };

    const handleClickView = (row) => {
        const { seguroAdjunto } = row;
        setOpenPreview(true);
        setViewDocumentData({
            ...seguroAdjunto,
            archivo: DecodeBase64ToBytesArray(seguroAdjunto.archivo)
        });
    };

    const handleClickEdit = (row) => {
        setNuevoSeguroData(row);
        handleOpenModal();
    };

    const columns = [
        {
            field: 'action',
            headerName: '',
            align: 'center',
            sortable: false,
            renderCell: ({ row }) => {
                const vigente = !moment(row.fechaHasta).isBefore(moment().format('YYYY-MM-DD'), 'day');

                return (
                    <PopoverModal>
                        <div className="popover-modal-content">
                            <div 
                                onClick={() => handleClickView(row)} 
                                aria-hidden='true'
                                className='item-option clickable-option'
                            >
                                <VisibilityIcon className='edit-icon' />
                                <span className='toggle-action'>Ver Archivo</span>
                            </div>
                            {vigente && allowedWrite && (
                                <div 
                                    onClick={() => handleClickEdit(row)} 
                                    aria-hidden='true'
                                    className='item-option clickable-option'
                                >
                                    <EditIcon className='edit-icon' />
                                    <span className='toggle-action'>Editar</span>
                                </div>
                            )}
                        </div>
                    </PopoverModal>
                );
            }
        },
        {
            field: 'fechaDesde',
            headerName: 'Fecha desde',
            type: 'date',
            flex: 2,
            sortable: false
        },
        {
            field: 'fechaHasta',
            headerName: 'Fecha hasta',
            type: 'date',
            flex: 2,
            sortable: false
        },
        {
            field: 'seguroAdjunto',
            headerName: 'Seguro de mala praxis',
            valueGetter: (seguroAdjunto) => seguroAdjunto?.titulo || '',
            flex: 2,
            sortable: false
        }
    ];

    const CONFIGURATED_COLUMNS = useMemo(() => (generateColumnsDefaultConfiguration(columns)), [columns, allowedWrite]);

    const isValidDate = (date) => {
        if (date instanceof Date)
            return !isNaN(date.getTime());

        const dateObj = new Date(date);
        return !isNaN(dateObj.getTime());
    };

    const createDisabled = useMemo(() => {
        if (!nuevoSeguroData.fechaDesde || !nuevoSeguroData.fechaHasta || !nuevoSeguroData.seguroAdjunto || !allowedWrite || isProcessing)
            return true;

        const fechaDesdeValida = isValidDate(nuevoSeguroData.fechaDesde);
        const fechaHastaValida = isValidDate(nuevoSeguroData.fechaHasta);

        return !fechaDesdeValida || !fechaHastaValida;
    }, [nuevoSeguroData, allowedWrite, isProcessing]);

    const isFechaDesdeDisabled = () => {
        return !!nuevoSeguroData.idArchivo;
    };

    const isFechaHastaDisabled = () => {
        if (!nuevoSeguroData.idArchivo) return false;
        if (!nuevoSeguroData.fechaDesde) return false;
        return moment(nuevoSeguroData.fechaDesde, 'YYYY-MM-DD').isSameOrBefore(moment(), 'day');
    };

    return (
        <div className='generic-tab-container container-separate'>
            <Grid container sx={{ marginTop: '20px' }} rowSpacing={1}>
                <Grid size={{ xs: 12 }}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <h4 style={{ margin: 0 }}>Seguros de mala praxis</h4>
                        <CustomButton
                            label='Agregar Seguro'
                            variant='outlined'
                            onClick={handleOpenModal}
                            disabled={!allowedWrite || isProcessing}
                        />
                    </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <DataGridGenerico
                        rows={datosSeleccionFormData?.seguroMalaPraxis}
                        columns={CONFIGURATED_COLUMNS}
                        hideFooterPagination={false}
                        toolbar={false}
                        localeText={{ noRowsLabel: 'No se han cargado seguros' }}
                    />
                </Grid>
            </Grid>

            <CustomDialog
                title={nuevoSeguroData.idArchivo ? 'Editar seguro de mala praxis' : 'Nuevo seguro de mala praxis'}
                fullWidth
                maxWidth='sm'
                open={openModal}
                onAccept={handleSaveSeguro}
                onCancel={handleCloseModal}
                acceptText={nuevoSeguroData.idArchivo ? 'Guardar' : 'Agregar'}
                cancelText='Cancelar'
                acceptDisabled={createDisabled}
            >
                <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                        <RenderInputByTypeMobile
                            input={{
                                displayName: 'Fecha desde',
                                name: 'fechaDesde',
                                type: 'date',
                                disabled: isFechaDesdeDisabled()
                            }}
                            handleChange={handleChangeSeguro}
                            value={nuevoSeguroData?.fechaDesde}
                        />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <RenderInputByTypeMobile
                            input={{
                                displayName: 'Fecha hasta',
                                name: 'fechaHasta',
                                type: 'date',
                                disabled: isFechaHastaDisabled()
                            }}
                            handleChange={handleChangeSeguro}
                            value={nuevoSeguroData?.fechaHasta}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <RenderInputByTypeMobile
                            input={{
                                displayName: 'Seguro de mala praxis',
                                name: 'seguroAdjunto',
                                type: 'file',
                                disabled: isProcessing
                            }}
                            handleChange={handleChangeSeguroArchivo}
                            handleDelete={handleDeleteArchivo}
                            value={nuevoSeguroData?.seguroAdjunto}
                        />
                    </Grid>
                </Grid>
            </CustomDialog>
        </div>
    );
};